import { Filter, Loader2, MoreHorizontal, Package, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { PageHeader } from "../../components/ui/PageHeader";
import { formatRupiah } from "../../lib/format";
import { apiFetch } from "../../lib/session";
import { useAccount } from "../../context/AccountContext";

type Product = { id: string; name: string; description?: string; price: number; active: boolean; purchaseMode?: string; image?: string };
type Site = { revision: number; products: Product[]; [key: string]: any };

export function ProductsPage() {
  const { refresh } = useAccount();
  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [notice, setNotice] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const result = await apiFetch<{ site: Site }>("/api/dashboard/site");
      setSite(result.site);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const addDemoProduct = async () => {
    if (!site) return;
    setSaving(true);
    const product: Product = {
      id: `prod_${Date.now()}`,
      name: `Produk Baru ${site.products.length + 1}`,
      description: "Produk tambahan dari dashboard Siap Profesional.",
      price: 50000,
      active: true,
      purchaseMode: "cart",
      image: "📦",
    };
    try {
      const result = await apiFetch<{ site: Site }>("/api/dashboard/site", {
        method: "PUT",
        body: JSON.stringify({ site: { ...site, products: [...site.products, product] }, expectedRevision: site.revision }),
      });
      setSite(result.site);
      setNotice("Produk baru ditambahkan ke draft yang sama dengan website WhatsApp.");
      await refresh();
    } catch {
      setNotice("Produk belum berhasil ditambahkan. Muat ulang karena revision mungkin berubah melalui WhatsApp.");
    } finally {
      setSaving(false);
    }
  };

  const filtered = (site?.products || []).filter((product) => product.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="space-y-7">
      <PageHeader eyebrow="Katalog tersinkron" title="Produk" description="Produk pertama dari paket Siap Digital langsung tersedia. Tambahkan produk baru tanpa mengulang onboarding." actions={<button type="button" className="btn-primary" onClick={addDemoProduct} disabled={saving || loading}>{saving ? <Loader2 size={17} className="animate-spin" /> : <Plus size={17} />} {saving ? "Menambahkan..." : "Tambah produk demo"}</button>} />
      {notice && <div className="rounded-2xl border border-brand-100 bg-brand-50 p-4 text-sm font-bold text-brand-800">{notice}</div>}
      <section className="surface overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full max-w-md"><Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={17} /><input className="field !pl-11" placeholder="Cari nama produk..." value={query} onChange={(event) => setQuery(event.target.value)} /></div>
          <button type="button" className="btn-secondary"><Filter size={16} /> Filter</button>
        </div>
        {loading ? <div className="grid min-h-72 place-items-center"><Loader2 className="animate-spin text-brand-600" /></div> : <div className="overflow-x-auto">
          <table className="w-full min-w-[800px] text-left">
            <thead><tr className="bg-slate-50 text-[11px] font-black uppercase tracking-[0.14em] text-slate-400"><th className="px-6 py-4">Produk</th><th className="px-6 py-4">Harga</th><th className="px-6 py-4">Sumber</th><th className="px-6 py-4">Purchase mode</th><th className="px-6 py-4">Status</th><th className="px-6 py-4" /></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((product, index) => <tr key={product.id} className="hover:bg-brand-50/30"><td className="px-6 py-4"><div className="flex items-center gap-3"><span className={`grid h-12 w-12 place-items-center overflow-hidden rounded-2xl ${index % 2 ? "bg-accent-50 text-accent-700" : "bg-brand-50 text-brand-700"}`}>{typeof product.image === "string" && product.image.startsWith("data:image") ? <img src={product.image} alt="" className="h-full w-full object-cover" /> : <Package size={20} />}</span><div><p className="text-sm font-extrabold text-slate-800">{product.name}</p><p className="mt-1 text-xs text-slate-400">{index === 0 ? "Produk awal dari WhatsApp" : product.id}</p></div></div></td><td className="px-6 py-4 text-sm font-black text-slate-800">{formatRupiah(product.price)}</td><td className="px-6 py-4 text-sm font-semibold text-slate-600">{index === 0 ? "#CSJW" : "Dashboard"}</td><td className="px-6 py-4 text-sm text-slate-500">{product.purchaseMode || "cart"}</td><td className="px-6 py-4"><span className={product.active !== false ? "badge-success" : "rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-500"}>{product.active !== false ? "Aktif" : "Nonaktif"}</span></td><td className="px-6 py-4"><button type="button" className="grid h-9 w-9 place-items-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700"><MoreHorizontal size={18} /></button></td></tr>)}
            </tbody>
          </table>
        </div>}
      </section>
    </div>
  );
}
